/* Sign-in view.

   Rendered INSTEAD of the shell, not layered over it. Signed out there is no
   workspace underneath worth preserving, and an overlay would leave a tabbable
   rail and header behind the card.

   Owns the flow end to end — popup, token exchange, error wording — and reports
   success with the `Me` payload /api/auth/session already returned, so the shell
   boots without a second /api/me.

   The only Firebase-aware component in the app, and it is not very: core/firebase
   hands back an ID token or null, and this decides what to say about it. */

import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { shadowReset } from '../../design/base.js';
import { ApiError, createSession } from '../../core/api.js';
import { signInWithGoogle } from '../../core/firebase.js';
import type { Me } from '../../core/types.js';
import '../../component/g-button/g-button.js';
import '../../component/g-mark/g-mark.js';
import '../../component/g-notice/g-notice.js';

@customElement('g-signin')
export class GSignin extends LitElement {
  static override styles = [shadowReset, css`
    :host {
      flex: 1;
      display: grid;
      place-items: center;
      padding: var(--space-5);
      background: var(--bg);
    }
    .card {
      width: 100%;
      max-width: 340px;
      display: grid;
      gap: var(--space-3);
      justify-items: center;
      text-align: center;
      padding: var(--space-6) var(--space-5);
      border: 1px solid var(--border);
      border-radius: var(--radius-lg);
      background: var(--surface);
      box-shadow: var(--shadow-1);
    }
    /* Same construction as the header's wordmark, at display scale. Not shared:
       one is chrome inside a 56px bar, this is the only thing on the screen. */
    .brand {
      display: flex;
      align-items: center;
      gap: var(--space-2);
      font-family: var(--font-display);
      font-size: 1.6rem;
      font-weight: 800;
      letter-spacing: -0.01em;
      --mark-size: 24px;
      user-select: none;
    }
    .sub {
      margin: 0;
      color: var(--text-dim);
      font-family: var(--font-text);
      font-style: italic;
      font-size: 0.95rem;
    }
    g-button {
      margin-top: var(--space-2);
    }
    .note {
      margin: 0;
      color: var(--text-dim);
      font-size: 0.75rem;
    }
  `];

  @state() private busy = false;
  @state() private error = '';

  private async signIn(): Promise<void> {
    this.busy = true;
    this.error = '';
    try {
      const idToken = await signInWithGoogle();
      // null = the popup was closed. A deliberate cancellation gets no error.
      if (idToken === null) return;
      const me = await createSession(idToken);
      this.dispatchEvent(new CustomEvent<Me>('signed-in', {
        detail: me, bubbles: true, composed: true,
      }));
    } catch (e) {
      // 403 is the server's policy answer — invite-only when OPEN_SIGNUP is
      // off, "a verified Google email is required" when it's on. Show its
      // wording rather than a hardcoded one, which would be wrong in whichever
      // mode it wasn't written for.
      this.error = e instanceof ApiError && e.status === 403
        ? e.message
        : 'Sign-in failed. Try again.';
    } finally {
      this.busy = false;
    }
  }

  override render() {
    return html`
      <div class="card">
        <div class="brand"><g-mark></g-mark>grain</div>
        <p class="sub">A reading room for everything you save.</p>

        <!-- No "full" attribute: it sets flex:1, which does nothing in this grid,
             and stretching the host instead would left-align the label — the
             inner button is text-align:left because the rail's rows need it. A
             content-width CTA centred by the grid needs no change to the
             primitive. -->
        <g-button
          variant="primary"
          ?disabled=${this.busy}
          @click=${this.signIn}
        >${this.busy ? 'Signing in…' : 'Sign in with Google'}</g-button>

        ${this.error ? html`<g-notice tone="danger">${this.error}</g-notice>` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'g-signin': GSignin;
  }
}
